import { CallState, ConferenceState, SAPContactSearchState, CallDirection } from '../../types';
import { SapContact } from '../../services/sap-cti.service';
import { formatPhoneForDisplay } from '../../utils/format-phone.util';
import { SearchParams } from './SearchForm';
import { ManualSearchState } from './sap-contact.types';

export type { CallDirection };

export type PanelMode = 'transfer' | 'conference' | null;

export interface CallDisplayContext {
  isOutboundCall?: boolean;
  outboundPhone?: string | null;
  outboundReferenceId?: string | null;
}

export interface CallCardProps {
  callState: CallState;
  conferenceState: ConferenceState;
  isRinging: boolean;
  isMuted: boolean;
  isRecording: boolean;
  isAgentLegActive?: boolean;
  callError: string | null;
  callDisplayContext?: CallDisplayContext;
  sapContactState?: SAPContactSearchState;
  manualSearchState?: ManualSearchState;
  onAccept: () => void;
  onReject: () => void;
  onTransfer: () => void;
  onConference: () => void;
  onCallEnd: () => void;
  onContactOpened?: (contact: SapContact) => void;
  onManualSearch?: (params: SearchParams) => void;
  linkedSapContactId?: string | null;
}

export function resolveCallDirection(
  callState: CallState,
  context?: CallDisplayContext
): CallDirection {
  if (context?.isOutboundCall || callState.direction === 'OUTBOUND') {
    return 'OUTBOUND';
  }
  return 'INBOUND';
}

function isPhoneDisplayValue(
  value: string,
  callState: CallState,
  context?: CallDisplayContext
): boolean {
  return (
    value === callState.callerNumber || value === callState.dnis || value === context?.outboundPhone
  );
}

function formatCallDisplayValue(
  value: string,
  callState: CallState,
  context?: CallDisplayContext
): string {
  if (value === 'Unknown') {
    return value;
  }
  if (isPhoneDisplayValue(value, callState, context)) {
    return formatPhoneForDisplay(value);
  }
  return value;
}

export function getCallPrimaryDisplay(callState: CallState, context?: CallDisplayContext): string {
  const direction = resolveCallDirection(callState, context);
  let raw: string;
  if (direction === 'OUTBOUND') {
    raw =
      context?.outboundPhone ||
      callState.callerNumber ||
      callState.dnis ||
      callState.callerName ||
      'Unknown';
  } else {
    raw = callState.callerName || callState.callerNumber || callState.dnis || 'Unknown';
  }
  return formatCallDisplayValue(raw, callState, context);
}

export function getCallSecondaryDisplay(
  callState: CallState,
  primaryDisplay: string,
  context?: CallDisplayContext
): string | null {
  if (resolveCallDirection(callState, context) === 'OUTBOUND') {
    return null;
  }

  if (
    callState.callerName &&
    callState.callerNumber &&
    callState.callerName !== callState.callerNumber &&
    primaryDisplay === callState.callerName
  ) {
    return formatPhoneForDisplay(callState.callerNumber);
  }

  return null;
}

export function getCallerInitial(callState: CallState, callerDisplay: string): string {
  if (resolveCallDirection(callState) === 'INBOUND' && callState.callerName) {
    return callState.callerName.charAt(0).toUpperCase();
  }
  return callerDisplay.charAt(0) === '+' ? callerDisplay.charAt(1) : callerDisplay.charAt(0);
}
